import { useContext, useEffect, useState } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import HouseIcon from "@mui/icons-material/House";
import SubscriptionsOutlinedIcon from "@mui/icons-material/SubscriptionsOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import FingerprintOutlinedIcon from "@mui/icons-material/FingerprintOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { AuthContext } from "../../components/AuthContext";
import { useModules } from "../../context/ModuleContext";

const Item = ({ title, to, icon, selected, setSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const SideNav = ({ compactLayout = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down("md"));
  
  const [isCollapsed, setIsCollapsed] = useState(compactLayout || isTabletOrBelow);
  const [selected, setSelected] = useState("Dashboard");
  const { displayName, role } = useContext(AuthContext);
  const location = useLocation();
  const { isModuleEnabled } = useModules();
  
  const isGlobalAdmin = role === "global_admin";
  const isBranchAdmin = role === "branch_admin";
  const isHpcAdmin = role === "hpc_admin";
  const isAdmin = isGlobalAdmin || isBranchAdmin;
  const isMember = role === "member";

  // Resolved conflict: handles both prop-driven layout and screen resizing
  useEffect(() => {
    setIsCollapsed(compactLayout || isTabletOrBelow);
  }, [compactLayout, isTabletOrBelow]);

  useEffect(() => {
    const path = location.pathname;
    switch (path) {
      case "/home":
        setSelected("Dashboard");
        break;
      case "/attendance-dashboard":
        setSelected("Attendance Dashboard");
        break;
      case "/teba/overview":
        setSelected("TEBA Overview");
        break;
      case "/manage_member":
        setSelected("Manage Member");
        break;
      case "/member_list":
        setSelected("Member List");
        break;
      case "/view_member":
        setSelected("Family Profile");
        break;
      case "/member_payment":
        setSelected("Payment History");
        break;
      case "/teba/payment-receive":
        setSelected("Payment Receive");
        break;
      case "/update_family_owner":
        setSelected("Update Family Owner");
        break;
      case "/family_list":
        setSelected("Family List");
        break;
      case "/manage_branch":
        setSelected("Manage Branch");
        break;
      case "/branch_list":
        setSelected("Branch List");
        break;
      case "/manage_subscription":
        setSelected("Manage Subscription");
        break;
      case "/subscription_list":
        setSelected("Subscription List");
        break;
      case "/manage_discipline":
        setSelected("Manage Discipline");
        break;
      case "/discipline_list":
        setSelected("Discipline List");
        break;
      case "/generate_reports":
        setSelected("Generate Reports");
        break;
      case "/hpc/overview":
        setSelected("HPU Overview");
        break;
      case "/hpc/dashboard":
        setSelected("HPU Dashboard");
        break;
      case "/hpc/members":
        setSelected("HPC Members");
        break;
      case "/payment-receive":
        setSelected("HPU Payment Receive");
        break;
      case "/hpc/assessments":
        setSelected("HPC Assessments");
        break;
      case "/hpc/assessments/new":
        setSelected("HPC Assessments");
        break;
      case "/hpc/manual-sms":
        setSelected("Manual SMS");
        break;
      case "/hpc/attendance":
        setSelected("HPU Attendance");
        break;
      case "/hpc/attendance-device":
        setSelected("Attendance Device");
        break;
      default:
        if (path.startsWith("/hpc/members/")) {
          setSelected("HPC Members");
          break;
        }
        if (path.startsWith("/hpc/assessments/")) {
          setSelected("HPC Assessments");
          break;
        }
        if (path === "/hpc/promotions" || path.startsWith("/hpc/promotions/")) {
          setSelected("Promotions");
          break;
        }
        setSelected(null);
        break;
    }
  }, [location.pathname]);

  return (
    <Box
      sx={{
        height: "100dvh",
        flexShrink: 0,
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .pro-inner-item:hover": {
          color: "#868dfb !important",
        },
        "& .pro-menu-item.active": {
          color: "#6870fa !important",
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Typography variant="h3" color={colors.grey[100]}>
                  TEBA Solution
                </Typography>
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {!isCollapsed && (
            <Box mb="25px">
              <Box display="flex" justifyContent="center" alignItems="center">
                <img
                  alt="profile-user"
                  width="100px"
                  height="100px"
                  src={`../../assets/user.png`}
                  style={{ cursor: "pointer", borderRadius: "50%" }}
                />
              </Box>
              <Box textAlign="center">
                <Typography
                  variant="h2"
                  color={colors.grey[100]}
                  fontWeight="bold"
                  sx={{ m: "10px 0 0 0" }}
                >
                  {displayName || ""}
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[500]}>
                  {role || ""}
                </Typography>
              </Box>
            </Box>
          )}

          <Box paddingLeft={isCollapsed ? undefined : "10%"}>
            {isHpcAdmin && isModuleEnabled("hpc") && (
              <>
                <Typography
                  variant="h6"
                  color={colors.grey[300]}
                  sx={{ m: "15px 0 5px 20px" }}
                >
                  HPC
                </Typography>
                <Item
                  title="HPU Overview"
                  to="/hpc/overview"
                  icon={<InsightsOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="HPU Dashboard"
                  to="/hpc/dashboard"
                  icon={<DashboardOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="HPC Members"
                  to="/hpc/members"
                  icon={<FitnessCenterOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="HPU Payment Receive"
                  to="/payment-receive"
                  icon={<PaymentsOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="HPC Assessments"
                  to="/hpc/assessments"
                  icon={<AssignmentOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="Manual SMS"
                  to="/hpc/manual-sms"
                  icon={<SmsOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="Promotions"
                  to="/hpc/promotions"
                  icon={<LocalOfferOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                {isModuleEnabled("zkteco") && (
                  <>
                    <Item
                      title="HPU Attendance"
                      to="/hpc/attendance"
                      icon={<EventAvailableOutlinedIcon />}
                      selected={selected}
                      setSelected={setSelected}
                    />
                    <Item
                      title="Attendance Device"
                      to="/hpc/attendance-device"
                      icon={<FingerprintOutlinedIcon />}
                      selected={selected}
                      setSelected={setSelected}
                    />
                  </>
                )}
              </>
            )}

            {/* Dashboard - only show if dashboard module is enabled */}
            {isGlobalAdmin && isModuleEnabled("dashboard") && (
              <Item
                title="Dashboard"
                to="/home"
                icon={<HomeOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
              />
            )}

            {isAdmin && isModuleEnabled("dashboard") && (
              <Item
                title="TEBA Overview"
                to="/teba/overview"
                icon={<InsightsOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
              />
            )}

            {isAdmin && isModuleEnabled("attendance") && (
              <Item
                title="Attendance Dashboard"
                to="/attendance-dashboard"
                icon={<EventAvailableOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
              />
            )}

            {/* Members Section - only show if basic module is enabled */}
            {isAdmin && isModuleEnabled("basic") && (
              <>
                <Typography
                  variant="h6"
                  color={colors.grey[300]}
                  sx={{ m: "15px 0 5px 20px" }}
                >
                  Members
                </Typography>
                <Item
                  title="Manage Member"
                  to="/manage_member"
                  icon={<PersonOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="Member List"
                  to="/member_list"
                  icon={<ListAltOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                {isModuleEnabled("payments") && (
                  <Item
                    title="Payment Receive"
                    to="/teba/payment-receive"
                    icon={<PaymentsOutlinedIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}
              </>
            )}

            {isMember && isModuleEnabled("basic") && (
              <>
                <Typography
                  variant="h6"
                  color={colors.grey[300]}
                  sx={{ m: "15px 0 5px 20px" }}
                >
                  Family
                </Typography>
                <Item
                  title="Family Profile"
                  to="/view_member"
                  icon={<FamilyRestroomOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="Member List"
                  to="/member_list"
                  icon={<ListAltOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="Payment History"
                  to="/member_payment"
                  icon={<SubscriptionsOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
              </>
            )}

            {/* Branch Section - only show if basic module is enabled */}
            {isGlobalAdmin && isModuleEnabled("basic") && (
              <>
                <Typography
                  variant="h6"
                  color={colors.grey[300]}
                  sx={{ m: "15px 0 5px 20px" }}
                >
                  Branch
                </Typography>

                <Item
                  title="Manage Branch"
                  to="/manage_branch"
                  icon={<HouseIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="Branch List"
                  to="/branch_list"
                  icon={<ListAltOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
              </>
            )}

            {/* Subscriptions Section - only show if basic module is enabled */}
            {isAdmin && isModuleEnabled("basic") && (
              <>
                <Typography
                  variant="h6"
                  color={colors.grey[300]}
                  sx={{ m: "15px 0 5px 20px" }}
                >
                  Subscriptions
                </Typography>

                {isGlobalAdmin && (
                  <Item
                    title="Manage Subscription"
                    to="/manage_subscription"
                    icon={<SubscriptionsOutlinedIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}
                <Item
                  title="Subscription List"
                  to="/subscription_list"
                  icon={<ListAltOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
              </>
            )}

            {/* Disciplines Section - only show if basic module is enabled */}
            {isAdmin && isModuleEnabled("basic") && (
              <>
                <Typography
                  variant="h6"
                  color={colors.grey[300]}
                  sx={{ m: "15px 0 5px 20px" }}
                >
                  Disciplines
                </Typography>

                {isGlobalAdmin && (
                  <Item
                    title="Manage Discipline"
                    to="/manage_discipline"
                    icon={<SubscriptionsOutlinedIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}
                <Item
                  title="Discipline List"
                  to="/discipline_list"
                  icon={<ListAltOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
              </>
            )}

            {/* Reports Section - only show if reports module is enabled */}
            {isAdmin && isModuleEnabled("reports") && (
              <>
                <Typography
                  variant="h6"
                  color={colors.grey[300]}
                  sx={{ m: "15px 0 5px 20px" }}
                >
                  Reports
                </Typography>

                <Item
                  title="Generate Reports"
                  to="/generate_reports"
                  icon={<AssessmentOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
              </>
            )}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default SideNav;